import {
  type GetUserPreferencesResp,
  type UiPropertyValueSchema,
  type UpdateSurfacePreferencesResp,
  type UserPreferences,
  type UserPreferencesRequired,
  userPreferencesSchema,
  type UserPreferenceSurfaces,
} from "@/types/userPreferences.ts";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { badgeQueries } from "@/hooks/useBadge.ts";
import { api } from "@/lib/http.ts";
import * as ld from "lodash-es";
import { useEffect } from "react";
import { isSharedThreadPath } from "@/lib/sharedThread.ts";

export const PREFERENCES_STORAGE_KEY = "user_preferences";

/**
 * Merge customizer that treats arrays as leaves. lodash's `merge` splices
 * arrays together index by index, so a stored list of two keys laid over a
 * default of seven would keep the five it did not cover — and a stored `[]`
 * (a disabled shortcut) would change nothing at all. The server's deep-merge
 * replaces arrays, and this keeps the client reading the same value.
 */
const replaceArrays = (_previous: unknown, next: unknown): unknown =>
  Array.isArray(next) ? next : undefined;

/** Try to get cached preferences from local storage */
const getLocalPreferences = (): GetUserPreferencesResp | undefined => {
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : undefined;
  } catch (error) {
    console.error("Failed to parse stored preferences:", error);
    return undefined;
  }
};

/** Query factory for preferences queries */
export const preferencesQueries = {
  all: () => ["preferences"] as const,
  user: () =>
    queryOptions<GetUserPreferencesResp>({
      queryKey: [...preferencesQueries.all(), "user"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<GetUserPreferencesResp>("/api/a/users/me/preferences", {
          signal,
        });
        return response.data;
      },
      placeholderData: getLocalPreferences,
      // Skip fetching for shared thread views (unauthenticated users) — the
      // endpoint 401s without a session. Uses the non-hook path check since
      // this can run before the Router is mounted.
      enabled: !isSharedThreadPath(),
    }),
};

/**
 * Hook to fetch and manage user preferences
 * Uses local storage as a cache when preferences haven't loaded from the backend.
 *
 * @see useSurfacePreferences for general usage
 */
export const usePreferencesRequest = () => {
  const query = useQuery(preferencesQueries.user());

  // Persist to localStorage when data changes
  useEffect(() => {
    if (query.data) {
      try {
        localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(query.data));
      } catch (error) {
        console.error("Failed to store preferences in localStorage:", error);
      }
    }
  }, [query.data]);

  // TODO: Subscribe to socket.io events for real-time preference updates

  return query;
};

/** Get the default preference values for all properties on a surface. */
// A property's default value — recursing into object groups to build the nested
// defaults object (leaves carry their own `default`).
const getPropertyDefault = (schema: UiPropertyValueSchema): unknown =>
  schema.propertyType === "object"
    ? ld.mapValues(schema.properties, getPropertyDefault)
    : schema.default;

const getSurfaceDefaults = <Surface extends keyof UserPreferencesRequired>(
  surface: Surface,
): UserPreferencesRequired[Surface] => {
  if (!(surface in userPreferencesSchema)) {
    throw new Error(`Surface ${surface} is not a supported surface`);
  }

  // @ts-expect-error this is a complex type and TS is struggling, but we have other checks to ensure this is safe
  return ld.mapValues(userPreferencesSchema[surface].properties, getPropertyDefault);
};

/**
 * Hook to access all preferences for a surface (e.g., 'home', 'global').
 *
 * Merges preferences with local defaults, to ensure preferences are non-optional.
 */
export const useSurfacePreferences = <Surface extends keyof UserPreferencesRequired>(
  surface: Surface,
): UserPreferencesRequired[Surface] => {
  const { data } = usePreferencesRequest();

  // Locally merge the defaults with the API values, to ensure valid preferences are non-optional.
  // Even though our API should do this merging as well, doing it locally ensures that preferences are non-optional
  // and helps with: loading, backwards compat, and avoiding app crashes if the preferences endpoint is not working.
  // We'd prefer to do this vs making them optional or requiring a local default arg in the caller, that's just more defaults to manage.
  // Deep-merge so a partially-stored nested group (e.g. one channel toggled
  // under a tier) still fills in the other leaves' defaults.
  const surfacePreferences = ld.mergeWith(
    {},
    getSurfaceDefaults(surface),
    data?.preferences?.[surface],
    replaceArrays,
  );

  return surfacePreferences;
};

/**
 * Hook to access a single preferences property for a surface (e.g., 'home', 'global').
 *
 * Merges preferences with local defaults, to ensure preferences are non-optional.
 */
export const useSurfacePreferenceProperty = <
  Surface extends keyof UserPreferencesRequired,
  PreferenceProperty extends keyof UserPreferencesRequired[Surface],
>(
  surface: Surface,
  property: PreferenceProperty,
): UserPreferencesRequired[Surface][PreferenceProperty] => {
  const surfacePreferences = useSurfacePreferences(surface);

  if (!(property in surfacePreferences)) {
    throw new Error(
      `Preference ${String(
        property,
      )} is not a valid preference for surface ${surface}. No value present from our API or in the defaults.`,
    );
  }

  return surfacePreferences[property];
};

/**
 * Hook to update preferences for a specific surface.
 *
 * Applies the delta to the React Query cache optimistically before the POST,
 * so the UI reflects the change within the same render as the click. On
 * success the cache is replaced with the canonical server response; on error
 * it rolls back to the pre-mutation snapshot.
 *
 * The delta is deep-partial, matching the server's deep-merge: a caller sends
 * only the leaves it changed, so two tabs editing different leaves of one
 * object group cannot overwrite each other with stale copies of the rest.
 *
 * @param surface The surface to update preferences for (e.g., 'home', 'global')
 * @returns A function to update preferences that returns a promise of the updated preferences
 */
export const useUpdateSurfacePreferences = <Surface extends UserPreferenceSurfaces>(
  surface: Surface,
) => {
  const queryClient = useQueryClient();
  const mutationKey = ["updateSurfacePreferences", surface];
  // Counts this mutation too: the callbacks below run while it is still pending.
  const hasLaterSave = () => queryClient.isMutating({ mutationKey }) > 1;

  const mutation = useMutation<
    UpdateSurfacePreferencesResp,
    Error,
    NonNullable<UserPreferences[Surface]>,
    { snapshot: GetUserPreferencesResp | undefined }
  >({
    mutationKey,
    // Saves to one surface reach the server in the order they were made, so a
    // second toggle can't be overwritten by a first one that lands after it.
    // Each one's optimistic update still applies at once; only the requests
    // queue.
    scope: { id: `updateSurfacePreferences:${surface}` },
    mutationFn: async (preferenceDelta) => {
      const response = await api.post<UpdateSurfacePreferencesResp>(
        `/api/a/users/me/preferences/${surface}`,
        preferenceDelta,
      );
      return response.data;
    },
    onMutate: async (preferenceDelta) => {
      // Cancel in-flight GETs so a late response can't clobber our optimistic state
      await queryClient.cancelQueries({ queryKey: preferencesQueries.all() });

      const snapshot = queryClient.getQueryData<GetUserPreferencesResp>(
        preferencesQueries.user().queryKey,
      );

      queryClient.setQueryData<GetUserPreferencesResp>(
        preferencesQueries.user().queryKey,
        (currentPreferences) => {
          if (!currentPreferences) return currentPreferences;
          const currentSurface = currentPreferences.preferences?.[surface] ?? {};
          return {
            preferences: {
              ...currentPreferences.preferences,
              // Deep-merge so a partial nested delta (e.g. one channel under a
              // tier) doesn't replace the whole nested object — mirrors the
              // server's deep-merge in `updateUserPreferences`.
              [surface]: ld.mergeWith({}, currentSurface, preferenceDelta, replaceArrays),
            },
          };
        },
      );

      return { snapshot };
    },
    onError: (error, _delta, context) => {
      console.error(`Failed to update preferences for surface ${surface}:`, error);
      // The snapshot predates any save queued behind this one, so restoring it
      // would erase those too; the last save to settle refetches instead.
      if (context && !hasLaterSave()) {
        queryClient.setQueryData(preferencesQueries.user().queryKey, context.snapshot);
      }
    },
    onSuccess: (data) => {
      // The in-app badge toggles gate the count, so a successful notifications
      // save can change it without any accompanying thread socket event.
      if (surface === "notifications") {
        void queryClient.invalidateQueries({ queryKey: badgeQueries.all() });
      }
      // This response is missing the saves queued behind it. Painting it would
      // flip their rows back until they land, so the last one reconciles.
      if (hasLaterSave()) return;
      queryClient.setQueryData<GetUserPreferencesResp>(
        preferencesQueries.user().queryKey,
        (currentPreferences) => {
          if (!currentPreferences) return currentPreferences;
          return {
            preferences: {
              ...currentPreferences.preferences,
              [surface]: data.surfacePreferences[surface] ?? {},
            },
          };
        },
      );
    },
    onSettled: () => {
      if (hasLaterSave()) return;
      void queryClient.invalidateQueries({ queryKey: preferencesQueries.all() });
    },
  });

  return mutation.mutateAsync;
};
